import { cloneElement, isValidElement, type ComponentChildren } from "preact";
import { BRAND } from "./config";
import type { I18nKey } from "./i18n";

type T = (k: I18nKey) => string;

export function Header({ t, onLang, showLang = true }: { t: T; onLang: () => void; showLang?: boolean }) {
  return (
    <div class="bbg-header">
      <img class="bbg-logo" src={BRAND.logo} alt={t("brandName")} />
      <span class="bbg-badge">{t("freeConsult")}</span>
      {showLang ? (
        // The visible target-language text IS the accessible name (label-in-name);
        // a hardcoded English aria-label broke voice control in es mode.
        <button class="bbg-lang" onClick={onLang}>
          {t("c_lang")}
        </button>
      ) : null}
    </div>
  );
}

export function Progress({ total, current }: { total: number; current: number }) {
  return (
    <div class="bbg-progress" aria-hidden="true">
      {Array.from({ length: total }).map((_, i) => (
        <span key={i} class={i < current ? "done" : i === current ? "active" : ""} />
      ))}
    </div>
  );
}

export function Field(props: {
  /** Stable control id: associates the label (htmlFor) and error/help (aria-describedby). */
  id?: string;
  label: string;
  optional?: string;
  error?: string;
  help?: string;
  children: ComponentChildren;
}) {
  const descId = props.id ? (props.error ? `${props.id}-err` : props.help ? `${props.id}-help` : undefined) : undefined;
  const child =
    props.id && isValidElement(props.children)
      ? cloneElement(props.children, {
          id: props.id,
          "aria-invalid": props.error ? "true" : undefined,
          "aria-describedby": descId,
        })
      : props.children;
  return (
    <div class="bbg-field">
      <label htmlFor={props.id}>
        {props.label} {props.optional ? <span class="opt">({props.optional})</span> : null}
      </label>
      {child}
      {props.help && !props.error ? <div id={props.id ? `${props.id}-help` : undefined} class="bbg-help">{props.help}</div> : null}
      {props.error ? <div id={props.id ? `${props.id}-err` : undefined} class="bbg-err" role="alert">{props.error}</div> : null}
    </div>
  );
}

export function PrimaryButton(props: {
  children: ComponentChildren;
  onClick?: () => void;
  type?: "button" | "submit";
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: string;
}) {
  return (
    <button
      type={props.type ?? "button"}
      class="bbg-btn bbg-btn-primary"
      disabled={props.disabled || props.loading}
      onClick={props.onClick}
    >
      {props.loading ? (
        <>
          <span class="bbg-spin" /> {props.loadingLabel}
        </>
      ) : (
        props.children
      )}
    </button>
  );
}

export function BackButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button class="bbg-btn bbg-btn-ghost" onClick={onClick}>
      ← {label}
    </button>
  );
}

export function CheckMark() {
  return (
    <div class="bbg-check" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 6L9 17l-5-5" />
      </svg>
    </div>
  );
}
